import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

type Theme = "light" | "dark" | "system";

type ThemeProviderProps = {
    children: React.ReactNode;
    defaultTheme?: Theme;
    storageKey?: string;
};

type ThemeContextValue = {
    theme: Theme;
    resolvedTheme: "light" | "dark";
    setTheme: (theme: Theme) => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

function resolveTheme(theme: Theme): "light" | "dark" {
    if (theme === "system") {
        if (typeof window === "undefined") return "light";
        return window.matchMedia("(prefers-color-scheme: dark)").matches
            ? "dark"
            : "light";
    }
    return theme;
}

/**
 * TSGo ThemeProvider — lightweight, dependency-free.
 *
 * - Persists the user's choice to localStorage.
 * - Applies the appropriate class (`light` / `dark`) to <html>.
 * - Reacts to OS preference changes when `theme === "system"`.
 */
export function ThemeProvider({
    children,
    defaultTheme = "light",
    storageKey = "tsgo-theme",
}: ThemeProviderProps) {
    const [theme, setThemeState] = useState<Theme>(() => {
        if (typeof window === "undefined") return defaultTheme;
        const stored = window.localStorage.getItem(storageKey) as Theme | null;
        return stored ?? defaultTheme;
    });

    const resolvedTheme = useMemo(() => resolveTheme(theme), [theme]);

    useEffect(() => {
        const root = document.documentElement;
        root.classList.remove("light", "dark");
        root.classList.add(resolvedTheme);
    }, [resolvedTheme]);

    useEffect(() => {
        if (theme !== "system") return;
        const media = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = () => {
            const root = document.documentElement;
            root.classList.remove("light", "dark");
            root.classList.add(media.matches ? "dark" : "light");
        };
        media.addEventListener("change", handler);
        return () => media.removeEventListener("change", handler);
    }, [theme]);

    const setTheme = useCallback(
        (next: Theme) => {
            window.localStorage.setItem(storageKey, next);
            setThemeState(next);
        },
        [storageKey],
    );

    const value = useMemo<ThemeContextValue>(
        () => ({ theme, resolvedTheme, setTheme }),
        [theme, resolvedTheme, setTheme],
    );

    return (
        <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
    );
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
    return ctx;
}
